/*
  Cart drawer + cart page controller (Watercolor Atlas).

  - Exposes window.DWCart = { open, close, refresh }.
  - Listens for "cart:updated" (dispatched by dw-quick-add.js and dw-product.js
    with { detail: { cart } }) and re-renders every cart surface from /cart.js JSON.
  - Quantity steppers and remove links call /cart/change.js by line item key.
  - Order note saves to /cart/update.js on change.
  - Drawer: <dialog> slide-over, ESC closes, focus is trapped, page scroll locks,
    header cart links open the drawer instead of navigating.
  - Idempotent: safe to include the script tag more than once per page.
*/
(function () {
  'use strict';

  if (window.DWCart) return;

  var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  var drawer = null;
  var cartPath = root + 'cart';
  var currencyCode = 'USD';
  var lastFocused = null;
  var busy = false;
  var closeTimer = null;
  var lastRenderAt = 0;
  var lastCart = null;
  var ocuUiSince = 0;
  var lastAtc = null;

  function qs(selector, context) {
    return (context || document).querySelector(selector);
  }

  function qsa(selector, context) {
    return Array.prototype.slice.call((context || document).querySelectorAll(selector));
  }

  /* ------------------------------------------------------------------ *
   *  Formatting helpers
   * ------------------------------------------------------------------ */

  function formatMoney(cents, stripZeros) {
    var amount = (cents || 0) / 100;
    var whole = Boolean(stripZeros) && (cents || 0) % 100 === 0;
    var options = { style: 'currency', currency: currencyCode };
    if (whole) {
      options.minimumFractionDigits = 0;
      options.maximumFractionDigits = 0;
    }
    try {
      return new Intl.NumberFormat(document.documentElement.lang || undefined, options).format(amount);
    } catch (error) {
      return '$' + amount.toFixed(whole ? 0 : 2);
    }
  }

  function imageUrl(src, width) {
    if (!src) return '';
    try {
      var url = new URL(src, window.location.origin);
      url.searchParams.set('width', String(width));
      return url.toString();
    } catch (error) {
      return src;
    }
  }

  function escapeKey(key) {
    if (window.CSS && typeof window.CSS.escape === 'function') return window.CSS.escape(key);
    return String(key).replace(/["\\]/g, '\\$&');
  }

  /* ------------------------------------------------------------------ *
   *  Network
   * ------------------------------------------------------------------ */

  function fetchCart(isRetry) {
    /* One bounded retry: Shopify throttles /cart.js under burst (multiple
       apps refetch the cart on every change), and a single 429 shouldn't
       leave the drawer stale. */
    return fetch(root + 'cart.js', { headers: { Accept: 'application/json' } }).then(function (response) {
      if (!response.ok) throw new Error('cart fetch failed');
      return response.json();
    }).catch(function (err) {
      if (isRetry) throw err;
      return new Promise(function (resolve) { window.setTimeout(resolve, 1400); }).then(function () {
        return fetchCart(true);
      });
    });
  }

  function postJSON(url, payload) {
    return fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(payload)
    });
  }

  function dispatchUpdated(cart) {
    document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart: cart, source: 'dw-cart' } }));
  }

  function changeLine(key, quantity) {
    if (busy || !key) return;
    busy = true;
    setBusy(true);
    postJSON(root + 'cart/change.js', { id: key, quantity: quantity })
      .then(function (response) {
        if (!response.ok) {
          return response.json().then(function (data) {
            throw new Error((data && (data.description || data.message)) || 'The cart could not be updated');
          });
        }
        return response.json();
      })
      .then(function (cart) {
        clearError();
        render(cart);
        dispatchUpdated(cart);
      })
      .catch(function (error) {
        showError(error.message);
        return fetchCart().then(render);
      })
      .finally(function () {
        busy = false;
        setBusy(false);
      });
  }

  function saveNote(note) {
    postJSON(root + 'cart/update.js', { note: note }).catch(function () {});
  }

  /* ------------------------------------------------------------------ *
   *  Rendering
   * ------------------------------------------------------------------ */

  function buildLine(item) {
    var title = item.product_title || item.title || '';

    var li = document.createElement('li');
    li.className = 'dw-cart-line';
    li.setAttribute('data-dw-cart-line', '');
    li.setAttribute('data-key', item.key);
    li.setAttribute('data-quantity', String(item.quantity));

    var media = document.createElement('a');
    media.className = 'dw-cart-line__media';
    media.href = item.url;
    media.tabIndex = -1;
    media.setAttribute('aria-hidden', 'true');
    if (item.image) {
      var img = document.createElement('img');
      img.src = imageUrl(item.image, 168);
      img.alt = '';
      img.width = 84;
      img.height = 84;
      img.loading = 'lazy';
      media.appendChild(img);
    } else {
      var placeholder = document.createElement('span');
      placeholder.className = 'dw-cart-line__placeholder';
      media.appendChild(placeholder);
    }
    li.appendChild(media);

    var info = document.createElement('div');
    info.className = 'dw-cart-line__info';

    var titleLink = document.createElement('a');
    titleLink.className = 'dw-cart-line__title';
    titleLink.href = item.url;
    titleLink.textContent = title;
    info.appendChild(titleLink);

    if (item.variant_title && item.variant_title !== 'Default Title') {
      var variant = document.createElement('p');
      variant.className = 'dw-cart-line__variant';
      variant.textContent = item.variant_title;
      info.appendChild(variant);
    }

    if (item.selling_plan_allocation && item.selling_plan_allocation.selling_plan) {
      var plan = document.createElement('p');
      plan.className = 'dw-cart-line__plan';
      plan.textContent = item.selling_plan_allocation.selling_plan.name;
      info.appendChild(plan);
    }

    var qtyRow = document.createElement('div');
    qtyRow.className = 'dw-cart-line__qtyrow';

    var stepper = document.createElement('div');
    stepper.className = 'dw-cart-qty';

    var minus = document.createElement('button');
    minus.type = 'button';
    minus.className = 'dw-cart-qty__btn';
    minus.setAttribute('data-dw-qty-minus', '');
    minus.setAttribute('aria-label', 'Decrease quantity of ' + title);
    minus.textContent = '−';
    stepper.appendChild(minus);

    var input = document.createElement('input');
    input.className = 'dw-cart-qty__input';
    input.type = 'number';
    input.name = 'updates[]';
    input.setAttribute('data-dw-qty-input', '');
    input.value = String(item.quantity);
    input.min = '0';
    input.setAttribute('inputmode', 'numeric');
    input.setAttribute('aria-label', 'Quantity of ' + title);
    stepper.appendChild(input);

    var plus = document.createElement('button');
    plus.type = 'button';
    plus.className = 'dw-cart-qty__btn';
    plus.setAttribute('data-dw-qty-plus', '');
    plus.setAttribute('aria-label', 'Increase quantity of ' + title);
    plus.textContent = '+';
    stepper.appendChild(plus);

    qtyRow.appendChild(stepper);

    var remove = document.createElement('a');
    remove.className = 'dw-cart-line__remove';
    remove.setAttribute('data-dw-remove', '');
    remove.href = cartPath + '/change?id=' + encodeURIComponent(item.key) + '&quantity=0';
    remove.setAttribute('aria-label', 'Remove ' + title + ' from your cart');
    remove.textContent = 'Remove';
    qtyRow.appendChild(remove);

    info.appendChild(qtyRow);
    li.appendChild(info);

    var price = document.createElement('div');
    price.className = 'dw-cart-line__price';
    if (item.original_line_price > item.final_line_price) {
      var compare = document.createElement('s');
      compare.textContent = formatMoney(item.original_line_price);
      price.appendChild(compare);
    }
    var finalPrice = document.createElement('span');
    finalPrice.textContent = formatMoney(item.final_line_price);
    price.appendChild(finalPrice);
    li.appendChild(price);

    return li;
  }

  function renderLists(cart) {
    var active = document.activeElement;
    var focusKey = null;
    var focusRole = null;
    if (active && active.closest) {
      var activeLine = active.closest('[data-dw-cart-line]');
      if (activeLine) {
        focusKey = activeLine.getAttribute('data-key');
        if (active.hasAttribute('data-dw-qty-minus')) focusRole = '[data-dw-qty-minus]';
        else if (active.hasAttribute('data-dw-qty-plus')) focusRole = '[data-dw-qty-plus]';
        else if (active.hasAttribute('data-dw-qty-input')) focusRole = '[data-dw-qty-input]';
        else if (active.hasAttribute('data-dw-remove')) focusRole = '[data-dw-remove]';
      }
    }

    qsa('[data-dw-cart-items]').forEach(function (list) {
      list.textContent = '';
      cart.items.forEach(function (item) {
        list.appendChild(buildLine(item));
      });
    });

    if (focusKey && focusRole) {
      var scope = drawer && drawer.open ? drawer : document;
      var line = qs('[data-dw-cart-line][data-key="' + escapeKey(focusKey) + '"]', scope) || qs('[data-dw-cart-line]', scope);
      var target = line ? qs(focusRole, line) : null;
      if (target) target.focus();
    }
  }

  function renderShipbars(cart) {
    qsa('[data-dw-shipbar]').forEach(function (bar) {
      var threshold = parseInt(bar.getAttribute('data-threshold-cents'), 10);
      if (!threshold || threshold <= 0) return;
      var remaining = threshold - cart.total_price;
      var pct = Math.min(100, Math.round((cart.total_price / threshold) * 100));
      var fill = qs('[data-dw-shipbar-fill]', bar);
      var msg = qs('[data-dw-shipbar-msg]', bar);
      if (fill) fill.style.width = pct + '%';
      bar.classList.toggle('is-complete', remaining <= 0);
      if (msg) {
        msg.textContent = remaining > 0
          ? "You're " + formatMoney(remaining, true) + ' away from free shipping'
          : 'Free shipping unlocked';
      }
    });
  }

  function renderTotals(cart) {
    qsa('[data-dw-cart-title-count]').forEach(function (node) {
      node.textContent = String(cart.item_count);
    });
    qsa('[data-dw-cart-subtotal]').forEach(function (node) {
      node.textContent = formatMoney(cart.total_price);
    });
  }

  function toggleDrawerEmpty(cart) {
    if (!drawer) return;
    var filled = qs('[data-dw-cart-filled]', drawer);
    var empty = qs('[data-dw-cart-empty]', drawer);
    if (filled) filled.hidden = cart.item_count === 0;
    if (empty) empty.hidden = cart.item_count > 0;
  }

  function syncPage(cart) {
    var page = qs('[data-dw-cart-page]');
    if (!page) return;
    var pageIsEmptyState = page.hasAttribute('data-cart-empty');
    var cartIsEmpty = cart.item_count === 0;
    if (pageIsEmptyState !== cartIsEmpty) window.location.reload();
  }

  function render(cart) {
    if (!cart) return;
    if (cart.currency) currencyCode = cart.currency;
    lastCart = cart;
    lastRenderAt = Date.now();
    renderTotals(cart);
    renderShipbars(cart);
    renderLists(cart);
    toggleDrawerEmpty(cart);
    syncPage(cart);
  }

  function setBusy(state) {
    qsa('[data-dw-cart-items]').forEach(function (list) {
      list.classList.toggle('is-busy', state);
      if (state) list.setAttribute('aria-busy', 'true');
      else list.removeAttribute('aria-busy');
    });
  }

  function showError(message) {
    qsa('[data-dw-cart-error]').forEach(function (node) {
      node.textContent = message || 'The cart could not be updated';
      node.hidden = false;
    });
  }

  function clearError() {
    qsa('[data-dw-cart-error]').forEach(function (node) {
      node.textContent = '';
      node.hidden = true;
    });
  }

  /* ------------------------------------------------------------------ *
   *  Zipify OCU coexistence
   *
   *  When the OCU (one-click-upsell) cart-drawer app embed is enabled it
   *  intercepts cart links AND add-to-cart clicks (capture phase) to show
   *  its own drawer / pre-purchase offer modal. Two rules keep the store
   *  sellable alongside it:
   *
   *  1. Single drawer: while OCU's drawer exists, the theme <dialog> never
   *     opens - a modal dialog makes OCU's UI inert (dead checkout button).
   *     Cart links fall through to OCU; open() hands off to OCU's drawer
   *     through its public "ocu:cart:open" event (see openOcu).
   *  2. Rescue net: OCU's interception can dead-end (offer API hiccup =
   *     click swallowed, nothing added, no feedback; or its offer modal
   *     dismissed without OCU's own /cart/add ever firing). Every ATC click
   *     is recorded; if no OCU UI appears and no theme handler ran, the
   *     item is added first-party and the cart opened.
   * ------------------------------------------------------------------ */

  function ocuOwnsCart() {
    return Boolean(document.querySelector('.ocu-cart-drawer'));
  }

  function ocuInstance() {
    var zipify = window.Zipify;
    return (zipify && zipify.Cart && zipify.Cart.instance) || null;
  }

  function ocuUiOnScreen() {
    return Boolean(
      document.querySelector('.ocu-cart-drawer.ocu-visible') ||
      document.querySelector('.ocu-modal__root-container')
    );
  }

  function ocuReady() {
    /* OCU turns itself off when its settings call fails: it sets
       `unpublished` and unbinds every listener. Don't hand the cart to a
       drawer that can no longer open. */
    var instance = ocuInstance();
    if (!instance) return true; /* pre-instance build: the event path is still its API */
    return instance.unpublished !== true && instance.isLoaded !== false;
  }

  function openOcu() {
    /* Ask OCU to open through its own public event API ("ocu:cart:open" on
       window, answered synchronously with "ocu:cart:opened"), NOT by
       synthesising a click on a cart link.

       Why not the click: OCU's cart-link interceptor is an async listener
       (`await handleAddToCart(e)` before it ever calls preventDefault), and
       a click fired from script never yields a microtask checkpoint
       mid-dispatch - our own stack is still on it. So the old
       "did OCU call preventDefault?" test always read false, we opened the
       theme drawer too, and the shopper got two carts until watchOcu closed
       ours a poll later. The event API answers in the same tick, so the
       decision is exact. It also can't toggle OCU's drawer shut (its click
       path is a toggle) and leaves no click listener parked on document. */
    if (!ocuReady()) {
      openThemeDrawer();
      return;
    }

    var accepted = false;
    var onOpened = function () {
      accepted = true;
    };
    window.addEventListener('ocu:cart:opened', onOpened);
    try {
      window.dispatchEvent(new CustomEvent('ocu:cart:open', {
        detail: {
          /* What OCU passes itself when the cart icon is clicked; it stores
             this for focus return and its cart_viewed event. */
          triggerElement: qs('.dw-header__cart') || qs('a[href="' + cartPath + '"]') || null
        }
      }));
    } catch (error) {
      /* Old browser without the CustomEvent constructor, or an OCU listener
         that threw: the theme drawer below is the answer either way. */
    }
    window.removeEventListener('ocu:cart:opened', onOpened);

    var instance = ocuInstance();
    if (!accepted && !(instance && instance.isOpen)) {
      /* Nothing answered - OCU is present in the DOM but not listening
         (it unbinds itself on a settings/runtime error). Ours is the cart. */
      openThemeDrawer();
      return;
    }

    /* OCU accepted. Belt and braces: if its drawer somehow never paints the
       shopper would be left with no cart at all after adding, so check once
       the paint has had time to land. This can only fire while nothing of
       OCU's is on screen, and watchOcu still closes ours if OCU shows up
       afterwards - the "never two drawers" rule keeps the last word. */
    window.setTimeout(function () {
      if (drawer && drawer.open) return;
      if (ocuUiOnScreen()) return;
      var current = ocuInstance();
      if (current && current.isOpen) return;
      openThemeDrawer();
    }, 400);
  }

  function rescueAdd(entry) {
    if (entry.done) return;
    entry.done = true;
    var payload = { id: entry.id, quantity: entry.qty };
    if (entry.plan) payload.selling_plan = entry.plan;
    postJSON(root + 'cart/add.js', payload)
      .then(function (response) {
        if (!response.ok) throw new Error('rescue add failed');
        return fetchCart();
      })
      .then(function (cart) {
        if (cart) {
          render(cart);
          dispatchUpdated(cart);
        }
        open();
      })
      .catch(function () {});
  }

  function scheduleModalExitCheck() {
    /* The offer modal closed: resync, and if the cart is still EMPTY the
       modal swallowed the buyer's add - re-add it first-party. */
    var entry = lastAtc;
    window.setTimeout(function () {
      fetchCart().then(function (cart) {
        if (!cart) return;
        render(cart);
        dispatchUpdated(cart);
        if (
          cart.item_count === 0 &&
          entry && !entry.done &&
          Date.now() - entry.at < 120000 &&
          !document.querySelector('.ocu-modal__root-container')
        ) {
          rescueAdd(entry);
        }
      }).catch(function () {});
    }, 2500);
  }

  function watchOcu() {
    var drawerWasOpen = false;
    var modalWasUp = false;

    /* Poll instead of a MutationObserver: observer callbacks get pulled into
       (and amplify) app mutation storms - see the runaway-observer breaker in
       layout/theme.liquid. A 400ms poll is plenty for these transitions. */
    window.setInterval(function () {
      var ocuOpen = Boolean(document.querySelector('.ocu-cart-drawer.ocu-visible'));
      var modalUp = Boolean(document.querySelector('.ocu-modal__root-container'));
      if (ocuOpen || modalUp) ocuUiSince = Date.now();
      /* Never two drawers: ours is modal and would make OCU's inert */
      if (ocuOpen && drawer && drawer.open) close();
      if (drawerWasOpen && !ocuOpen) {
        /* OCU drawer closed: resync the header badge + cart page */
        refresh().then(function (cart) {
          if (cart) dispatchUpdated(cart);
        });
      }
      if (modalWasUp && !modalUp) scheduleModalExitCheck();
      drawerWasOpen = ocuOpen;
      modalWasUp = modalUp;
    }, 400);

    /* Record every add-to-cart click. Capture on window fires before OCU's
       document-level capture listener can swallow the event. */
    window.addEventListener('click', function (event) {
      if (!event.target || !event.target.closest) return;
      if (!ocuOwnsCart()) return;
      var button = event.target.closest('button[type="submit"], [data-dw-quick-add]');
      if (!button || button.disabled || button.hasAttribute('data-dw-notify-trigger')) return;

      var entry = null;
      if (button.hasAttribute('data-dw-quick-add')) {
        var quickId = parseInt(button.getAttribute('data-variant-id'), 10);
        if (quickId) entry = { id: quickId, qty: 1, plan: 0, at: Date.now() };
      } else {
        var form = button.form;
        if (!form || String(form.action).indexOf('/cart/add') === -1) return;
        var idInput = form.querySelector('[name="id"]');
        var variantId = idInput ? parseInt(idInput.value, 10) : 0;
        if (!variantId) return;
        var qty = form.querySelector('[name="quantity"]');
        var scope = form.closest('.shopify-section') || document;
        var plan = scope.querySelector('[data-dw-plan]:checked');
        entry = {
          id: variantId,
          qty: qty ? Math.max(1, parseInt(qty.value, 10) || 1) : 1,
          plan: plan && plan.value ? parseInt(plan.value, 10) : 0,
          at: Date.now()
        };
      }
      if (!entry) return;
      lastAtc = entry;
      window.setTimeout(function () {
        if (entry.done) return;
        if (window.__dwAtcHandled && window.__dwAtcHandled >= entry.at) return; /* theme JS added it */
        if (ocuUiSince >= entry.at) return; /* OCU responded; its flow owns the add */
        if (document.querySelector('.ocu-modal__root-container')) return;
        if (document.querySelector('.ocu-cart-drawer.ocu-visible')) return;
        rescueAdd(entry);
      }, 3000);
    }, true);
  }

  /* ------------------------------------------------------------------ *
   *  Drawer open / close
   * ------------------------------------------------------------------ */

  function open() {
    if (ocuOwnsCart()) {
      /* OCU's drawer is the cart UI while its app embed is enabled */
      openOcu();
      return;
    }
    openThemeDrawer();
  }

  function openThemeDrawer() {
    if (!drawer || typeof drawer.showModal !== 'function') {
      window.location.href = cartPath;
      return;
    }
    if (closeTimer) {
      window.clearTimeout(closeTimer);
      closeTimer = null;
    }
    if (drawer.open) {
      /* Reopened during the close animation: cancel the slide-out, stay open */
      drawer.classList.add('is-open');
      return;
    }
    lastFocused = document.activeElement;
    drawer.showModal();
    document.documentElement.classList.add('dw-cart-open');
    window.requestAnimationFrame(function () {
      drawer.classList.add('is-open');
    });
    var closeButton = qs('[data-dw-cart-close]', drawer);
    if (closeButton) closeButton.focus();
    /* Skip the refetch when the drawer was rendered moments ago (quick-add
       and PDP add already fetched the cart); halves /cart.js traffic per
       add, which is what trips Shopify's cart throttle. */
    if (Date.now() - lastRenderAt > 1500) refresh();
  }

  function close() {
    if (!drawer || !drawer.open) return;
    drawer.classList.remove('is-open');
    if (closeTimer) window.clearTimeout(closeTimer);
    closeTimer = window.setTimeout(function () {
      closeTimer = null;
      if (drawer.open) drawer.close();
    }, 320);
  }

  function refresh() {
    return fetchCart().then(function (cart) {
      render(cart);
      return cart;
    }).catch(function () {
      return null;
    });
  }

  function bindDrawer() {
    if (!drawer) return;

    drawer.addEventListener('close', function () {
      drawer.classList.remove('is-open');
      document.documentElement.classList.remove('dw-cart-open');
      if (lastFocused && typeof lastFocused.focus === 'function') lastFocused.focus();
      lastFocused = null;
    });

    /* ESC: animate out instead of snapping shut */
    drawer.addEventListener('cancel', function (event) {
      event.preventDefault();
      close();
    });

    /* Backdrop click closes */
    drawer.addEventListener('click', function (event) {
      if (event.target === drawer) close();
    });

    /* Focus trap (belt and braces on top of the modal dialog's inert page) */
    drawer.addEventListener('keydown', function (event) {
      if (event.key !== 'Tab') return;
      var focusables = qsa(
        'a[href], button:not([disabled]), input:not([disabled]), textarea:not([disabled]), summary, [tabindex]:not([tabindex="-1"])',
        drawer
      ).filter(function (el) {
        return el.getClientRects().length > 0;
      });
      if (!focusables.length) return;
      var first = focusables[0];
      var last = focusables[focusables.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    });
  }

  /* ------------------------------------------------------------------ *
   *  Delegated events
   * ------------------------------------------------------------------ */

  function stepQuantity(button, delta) {
    var line = button.closest('[data-dw-cart-line]');
    if (!line) return;
    var current = parseInt(line.getAttribute('data-quantity'), 10) || 0;
    var next = Math.max(0, current + delta);
    if (next === current) return;
    changeLine(line.getAttribute('data-key'), next);
  }

  function isCartLink(link) {
    if (link.getAttribute('href') === '#') return false;
    var path;
    try {
      path = new URL(link.href, window.location.origin).pathname;
    } catch (error) {
      return false;
    }
    return path === cartPath || path === cartPath + '/';
  }

  document.addEventListener('click', function (event) {
    if (!event.target || !event.target.closest) return;
    var target = event.target;

    var opener = target.closest('[data-dw-cart-open]');
    if (opener) {
      event.preventDefault();
      open();
      return;
    }

    var closer = target.closest('[data-dw-cart-close]');
    if (closer) {
      event.preventDefault();
      close();
      return;
    }

    var minus = target.closest('[data-dw-qty-minus]');
    if (minus) {
      event.preventDefault();
      stepQuantity(minus, -1);
      return;
    }

    var plus = target.closest('[data-dw-qty-plus]');
    if (plus) {
      event.preventDefault();
      stepQuantity(plus, 1);
      return;
    }

    var remove = target.closest('[data-dw-remove]');
    if (remove) {
      var removeLine = remove.closest('[data-dw-cart-line]');
      if (removeLine) {
        event.preventDefault();
        changeLine(removeLine.getAttribute('data-key'), 0);
      }
      return;
    }

    /* Header (and any other) cart links open the drawer instead of navigating */
    var link = target.closest('a[href]');
    if (
      link &&
      drawer &&
      isCartLink(link) &&
      !link.closest('[data-dw-cart-drawer]') &&
      !event.defaultPrevented &&
      !ocuOwnsCart() /* OCU intercepts cart links itself; never double-open */
    ) {
      event.preventDefault();
      open();
    }
  });

  document.addEventListener('change', function (event) {
    if (!event.target || !event.target.closest) return;

    var input = event.target.closest('[data-dw-qty-input]');
    if (input) {
      var line = input.closest('[data-dw-cart-line]');
      if (!line) return;
      var quantity = parseInt(input.value, 10);
      if (isNaN(quantity) || quantity < 0) {
        quantity = parseInt(line.getAttribute('data-quantity'), 10) || 1;
        input.value = String(quantity);
        return;
      }
      changeLine(line.getAttribute('data-key'), quantity);
      return;
    }

    var note = event.target.closest('[data-dw-cart-note]');
    if (note) saveNote(note.value);
  });

  document.addEventListener('cart:updated', function (event) {
    var detail = event.detail || {};
    if (detail.source === 'dw-cart') return;
    if (detail.cart) render(detail.cart);
    else refresh();
  });

  /* Optimistic line render: when an add succeeded but the /cart.js refresh
     was throttled, the add response's line item is merged into the last
     known cart so the drawer never shows a stale (or empty) cart for an
     item that IS in the cart. The next successful refresh corrects totals. */
  document.addEventListener('cart:item-added', function (event) {
    var item = event.detail && event.detail.item;
    if (!item || !item.key) return;
    try {
    var base = lastCart || { note: '', attributes: {}, currency: currencyCode, items: [] };
    var items = (base.items || []).slice();
    var replaced = false;
    for (var i = 0; i < items.length; i++) {
      if (items[i].key === item.key) { items[i] = item; replaced = true; break; }
    }
    if (!replaced) items.unshift(item);
    var count = 0;
    var total = 0;
    var original = 0;
    items.forEach(function (it) {
      count += it.quantity || 0;
      var line = it.final_line_price != null ? it.final_line_price : (it.line_price || 0);
      total += line;
      original += it.original_line_price != null ? it.original_line_price : line;
    });
    var merged = {};
    for (var k in base) merged[k] = base[k];
    merged.items = items;
    merged.item_count = count;
    merged.total_price = total;
    merged.items_subtotal_price = total;
    merged.original_total_price = original;
    render(merged);
    } catch (err) { /* optimistic render is best-effort, never break the drawer */ }
    /* Optimistic content is approximate: let the next drawer open still
       refetch (its bounded retry corrects totals once the throttle clears). */
    lastRenderAt = 0;
  });

  /* ------------------------------------------------------------------ *
   *  Init
   * ------------------------------------------------------------------ */

  function init() {
    drawer = qs('[data-dw-cart-drawer]');
    if (drawer) {
      cartPath = drawer.getAttribute('data-cart-url') || cartPath;
      currencyCode = drawer.getAttribute('data-currency') || currencyCode;
      bindDrawer();
    }
    watchOcu();
    /* Quantities save on change when JS is running; hide the fallback button.
       style.display too: .dw-btn's display rule outranks the hidden attribute. */
    qsa('[data-dw-cart-update]').forEach(function (button) {
      button.hidden = true;
      button.style.display = 'none';
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  window.DWCart = { open: open, close: close, refresh: refresh };
})();
